<template>
  <section
    class="coupons-page"
    aria-labelledby="coupons-heading"
    :aria-busy="loading"
  >
    <header class="coupons-heading">
      <h2 id="coupons-heading">我的优惠券</h2>
      <nav aria-label="优惠券状态" class="coupon-status-tabs">
        <button
          v-for="tab in statusTabs"
          :key="tab.code"
          type="button"
          :class="{ active: statusCode === tab.code }"
          :aria-pressed="statusCode === tab.code"
          @click="changeStatus(tab.code)"
        >
          {{ tab.label }}（{{
            counts[tab.count] === null ? "—" : counts[tab.count]
          }}）
        </button>
      </nav>
    </header>
    <div v-if="!identity" class="coupon-state">
      请先登录后查看本人优惠券。<router-link to="/login">前往登录</router-link>
    </div>
    <template v-else>
      <div class="coupon-toolbar">
        <div class="module-filter">
          <span>服务类型</span
          ><button
            type="button"
            :class="{ active: moduleId === null }"
            :aria-pressed="moduleId === null"
            @click="changeModule(null)"
          >
            全部</button
          ><button
            v-for="item in moduleOptions"
            :key="item.id"
            type="button"
            :class="{ active: moduleId === item.id }"
            :aria-pressed="moduleId === item.id"
            @click="changeModule(item.id)"
          >
            {{ item.name }}
          </button>
          <span v-if="moduleLoading" role="status">服务类型加载中…</span>
          <span v-else-if="moduleError" role="alert">
            {{ moduleError }}
            <button type="button" @click="loadModuleOptions">重试服务类型</button>
          </span>
        </div>
        <div class="coupon-sort">
          <button
            type="button"
            :class="{ active: sortCode === 2 }"
            @click="changeSort(2)"
          >
            即将到期优先<img
              src="@/assets/images/coupons/1224f.svg"
              alt=""
            /></button
          ><button
            type="button"
            :class="{ active: sortCode === 3 }"
            @click="changeSort(3)"
          >
            满减面额</button
          ><button
            type="button"
            :class="{ active: sortCode === 1 }"
            @click="changeSort(1)"
          >
            领取时间
          </button>
        </div>
        <label class="coupon-type"
          >券种<select
            aria-label="券种"
            v-model.number="discountTypeId"
            :disabled="discountTypeLoading || !!discountTypeError"
            @change="applyFilters"
          >
            <option :value="0">全部券种</option>
            <option v-for="item in discountTypeOptions" :key="item.id" :value="item.id">{{ item.name }}</option>
          </select>
          <span v-if="discountTypeLoading" role="status">券种加载中…</span>
          <span v-else-if="discountTypeError" role="alert">
            {{ discountTypeError }}
            <button type="button" @click="loadDiscountTypeOptions">重试券种</button>
          </span></label
        >
        <button type="button" class="coupon-help" @click="showHelp = true">
          <img src="@/assets/images/coupons/56b67.svg" alt="" />优惠券使用规则
        </button>
      </div>
      <div v-if="error" role="alert" class="coupon-state">
        <strong>优惠券加载失败</strong>
        <p>{{ error }}</p>
        <button type="button" @click="loadCoupons">重新加载</button>
      </div>
      <div v-else-if="loading" role="status" class="coupon-state">
        正在加载本人优惠券…
      </div>
      <div v-else-if="!items.length" class="coupon-state">
        <h3>暂无{{ currentStatusLabel }}优惠券</h3>
        <p>可调整状态或筛选条件查看已到账的优惠券。</p>
      </div>
      <div v-else class="coupon-grid">
        <article
          v-for="(coupon, index) in items"
          :key="coupon.couponId"
          class="coupon-card"
          :class="{
            unavailable: !canUse(coupon),
            expanded: expanded === coupon.couponId,
          }"
        >
          <div class="coupon-module">
            <span>{{ coupon.moduleName || "模块未提供" }}</span
            ><span v-if="coupon.occupied">已占用</span
            ><span v-else-if="coupon.statusCode !== 1">{{
              coupon.statusName
            }}</span>
          </div>
          <div class="coupon-benefit">
            <strong>{{ couponBenefit(coupon) }}</strong
            ><span>{{ couponThreshold(coupon) }}</span>
          </div>
          <div class="coupon-divider">
            <img
              v-if="index === 0"
              src="@/assets/images/coupons/d4611.png"
              alt=""
            /><img
              v-else-if="index < 4"
              src="@/assets/images/coupons/d8d82.png"
              alt=""
            /><img v-else src="@/assets/images/coupons/b063c.png" alt="" />
          </div>
          <img
            class="coupon-notch left"
            src="@/assets/images/coupons/04b61.svg"
            alt=""
          /><img
            class="coupon-notch right"
            src="@/assets/images/coupons/04b61.svg"
            alt=""
          />
          <h3>{{ coupon.templateName || "券名称未提供" }}</h3>
          <p class="coupon-summary">
            {{ coupon.moduleName || "适用模块未提供" }}
          </p>
          <p class="coupon-expiry">
            {{ coupon.expiresAt || "到期时间未提供" }} 到期
          </p>
          <p v-if="coupon.unavailableReason" class="coupon-reason">
            {{ coupon.unavailableReason }}
          </p>
          <footer>
            <button
              type="button"
              class="coupon-rule-toggle"
              :aria-expanded="expanded === coupon.couponId"
              @click="
                expanded = expanded === coupon.couponId ? null : coupon.couponId
              "
            >
              使用规则<img
                src="@/assets/images/coupons/49649.svg"
                alt=""
              /></button
            ><router-link
              v-if="canUse(coupon) && couponUseRoute(coupon)"
              :to="couponUseRoute(coupon)"
              class="coupon-use"
              >去使用</router-link
            ><span v-else class="coupon-disabled">{{
              coupon.occupied
                ? "已占用"
                : coupon.statusCode === 1
                ? "暂不可用"
                : coupon.statusName || "暂不可用"
            }}</span>
          </footer>
          <coupon-rules v-if="expanded === coupon.couponId" :coupon="coupon" />
        </article>
      </div>
      <footer v-if="!loading && !error" class="coupon-pagination">
        <p>
          共 {{ total }} 张{{ currentStatusLabel }}优惠券，当前显示
          {{ total ? (page - 1) * pageSize + 1 : 0 }}–{{
            Math.min((page - 1) * pageSize + items.length, total)
          }}
          张
        </p>
        <div>
          <button
            type="button"
            :disabled="page <= 1"
            aria-label="上一页"
            @click="changePage(page - 1)"
          >
            <img src="@/assets/images/coupons/6e2f0.svg" alt="" /></button
          ><span class="current-page" aria-current="page">{{ page }}</span
          ><button
            type="button"
            :disabled="page * pageSize >= total"
            aria-label="下一页"
            @click="changePage(page + 1)"
          >
            <img src="@/assets/images/coupons/7ccbc.svg" alt="" /></button
          ><span>{{ pageSize }} 张 / 页</span>
        </div>
      </footer>
      <div class="coupon-hints">
        <strong>温馨提示</strong>
        <p>下单时选择符合条件的优惠券，每笔订单限用一张，不可叠加使用。</p>
        <p>
          优惠券不可兑换现金，请在有效期内使用；具体适用范围以券面使用规则为准。
        </p>
        <router-link class="coupon-contact" to="/contact"
          >遇到问题？联系客服<img
            src="@/assets/images/coupons/feedc.svg"
            alt=""
        /></router-link>
      </div>
    </template>
    <el-dialog
      title="优惠券使用规则"
      :visible.sync="showHelp"
      width="520px"
      append-to-body
      ><p>
        优惠券按照发放时的规则及实际有效期使用，适用范围以每张券的使用规则为准。
      </p>
      <p>
        摄影服务券按符合范围的摄影、化妆及场地合计计算门槛与优惠，平台费及独立额外精修交易不参与。
      </p>
      <p>
        每笔预约最多使用一张，不可叠加；查询不占券，使用资格以提交时服务端校验为准。
      </p>
      <span slot="footer"
        ><el-button @click="showHelp = false">知道了</el-button></span
      ></el-dialog
    >
  </section>
</template>
<script>
import request from "@/tool/request";
import API from "@/tool/api";
import couponPresentation from "@/tool/couponPresentation.cjs";
export default {
  components: {
    CouponRules: () => import("@/components/coupons/CouponRules.vue"),
  },
  data() {
    return {
      items: [],
      total: 0,
      counts: { availableCount: null, usedCount: null, expiredCount: null },
      statusCode: 1,
      moduleId: null,
      discountTypeId: 0,
      sortCode: 2,
      page: 1,
      pageSize: 8,
      loading: false,
      error: "",
      expanded: null,
      showHelp: false,
      requestVersion: 0,
      disposed: false,
      moduleOptions: [],
      moduleLoading: false,
      moduleError: "",
      moduleRequestVersion: 0,
      discountTypeOptions: [],
      discountTypeLoading: false,
      discountTypeError: "",
      discountTypeRequestVersion: 0,
      statusTabs: [
        { code: 1, label: "可使用", count: "availableCount" },
        { code: 2, label: "已使用", count: "usedCount" },
        { code: 3, label: "已过期", count: "expiredCount" },
      ],
    };
  },
  computed: {
    identity() {
      const user = this.$store.state.user;
      return user.is_login && user.user_id
        ? `${user.user_id}:${user.sessionVersion}`
        : "";
    },
    currentStatusLabel() {
      return this.statusTabs.find((t) => t.code === this.statusCode).label;
    },
  },
  watch: {
    identity: {
      immediate: true,
      handler() {
        this.items = [];
        this.total = 0;
        this.counts = {
          availableCount: null,
          usedCount: null,
          expiredCount: null,
        };
        this.moduleOptions = [];
        this.discountTypeOptions = [];
        this.moduleId = null;
        this.statusCode = 1;
        this.discountTypeId = 0;
        this.sortCode = 2;
        this.page = 1;
        this.expanded = null;
        this.showHelp = false;
        this.loadModuleOptions();
        this.loadDiscountTypeOptions();
        this.loadCoupons();
      },
    },
  },
  beforeDestroy() {
    this.disposed = true;
    this.requestVersion++;
    this.moduleRequestVersion++;
    this.discountTypeRequestVersion++;
  },
  methods: {
    ...couponPresentation,
    loadModuleOptions() {
      return this.loadFilterOptions("module", API.coupons.modules, "服务类型");
    },
    loadDiscountTypeOptions() {
      return this.loadFilterOptions("discountType", API.coupons.discountTypes, "券种");
    },
    async loadFilterOptions(prefix, url, label) {
      const version = ++this[`${prefix}RequestVersion`],
        identity = this.identity;
      this[`${prefix}Error`] = "";
      if (!identity) {
        this[`${prefix}Loading`] = false;
        return;
      }
      this[`${prefix}Loading`] = true;
      try {
        const response = await request.get(url);
        if (
          this.disposed || identity !== this.identity ||
          version !== this[`${prefix}RequestVersion`]
        ) return;
        const payload = response.data || {};
        if (
          payload.code !== 200 || payload.success === false ||
          !Array.isArray(payload.data) ||
          !payload.data.every((item) => item && Number.isInteger(item.id) &&
            item.id > 0 && typeof item.name === "string" && item.name.trim())
        ) throw new Error(payload.message || `${label}加载失败`);
        this[`${prefix}Options`] = payload.data.map((item) => ({ id: item.id, name: item.name }));
      } catch (error) {
        if (!this.disposed && identity === this.identity &&
          version === this[`${prefix}RequestVersion`]) {
          this[`${prefix}Error`] = `${label}加载失败，请重试`;
        }
      } finally {
        if (!this.disposed && version === this[`${prefix}RequestVersion`])
          this[`${prefix}Loading`] = false;
      }
    },
    canUse(coupon) {
      return (
        coupon.statusCode === 1 &&
        coupon.selectable === true &&
        !coupon.occupied
      );
    },
    changeStatus(code) {
      this.statusCode = code;
      this.applyFilters();
    },
    changeModule(id) {
      this.moduleId = id;
      this.applyFilters();
    },
    changeSort(code) {
      this.sortCode = code;
      this.applyFilters();
    },
    applyFilters() {
      this.page = 1;
      this.loadCoupons();
    },
    changePage(page) {
      if (page >= 1 && page <= Math.ceil(this.total / this.pageSize)) {
        this.page = page;
        this.loadCoupons();
      }
    },
    async loadCoupons() {
      const version = ++this.requestVersion,
        identity = this.identity;
      this.items = [];
      this.expanded = null;
      this.error = "";
      if (!identity) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.counts = {
        availableCount: null,
        usedCount: null,
        expiredCount: null,
      };
      const params = {
        statusCode: this.statusCode,
        sortCode: this.sortCode,
        page: this.page,
        pageSize: this.pageSize,
      };
      if (this.moduleId !== null) params.moduleId = this.moduleId;
      if (this.discountTypeId)
        params.discountTypeId = this.discountTypeId;
      try {
        const response = await request.get(API.coupons.page, { params });
        if (
          this.disposed ||
          identity !== this.identity ||
          version !== this.requestVersion
        )
          return;
        const payload = response.data || {};
        if (
          payload.code !== 200 ||
          payload.success === false ||
          !payload.data ||
          !Array.isArray(payload.data.items)
        )
          throw new Error(payload.message || "暂时无法查询优惠券，请重试");
        const data = payload.data;
        if (
          ![
            "total",
            "availableCount",
            "usedCount",
            "expiredCount",
            "page",
            "pageSize",
          ].every((key) => Number.isInteger(data[key]) && data[key] >= 0) ||
          data.page < 1 ||
          data.pageSize < 1 ||
          data.pageSize > 100
        )
          throw new Error("优惠券响应不完整，请重新加载");
        this.items = data.items;
        this.total = data.total;
        this.page = data.page;
        this.pageSize = data.pageSize;
        this.counts = {
          availableCount: data.availableCount,
          usedCount: data.usedCount,
          expiredCount: data.expiredCount,
        };
      } catch (error) {
        if (
          !this.disposed &&
          identity === this.identity &&
          version === this.requestVersion
        ) {
          this.error =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            "优惠券加载失败，请重试";
          this.counts = {
            availableCount: null,
            usedCount: null,
            expiredCount: null,
          };
          this.total = 0;
        }
      } finally {
        if (!this.disposed && version === this.requestVersion)
          this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.coupons-page {
  color: #24242a;
  font-size: 13px;
}
.coupons-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 6px;
}
.coupons-heading h2 {
  font-size: 24px;
  line-height: 32px;
  margin: 0;
  font-weight: 700;
}
.coupon-status-tabs {
  display: flex;
  gap: 28px;
}
.coupons-page button {
  font: inherit;
  cursor: pointer;
  background: transparent;
  border: 0;
  color: #777780;
  min-height: 44px;
  padding: 0;
}
.coupons-page button:focus-visible,
.coupons-page a:focus-visible,
.coupons-page select:focus-visible {
  outline: 2px solid #6b5dd5;
  outline-offset: 3px;
}
.coupon-status-tabs button {
  position: relative;
  white-space: nowrap;
  font-size: 14px;
}
.coupon-status-tabs .active {
  color: #6b5dd5;
  font-weight: 600;
}
.coupon-status-tabs .active:after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 32px;
  height: 3px;
  background: #7c6ff0;
  border-radius: 3px;
}
.coupon-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 6px;
  font-size: 12px;
}
.module-filter,
.coupon-sort {
  display: flex;
  gap: 12px;
  align-items: center;
}
.module-filter > span {
  color: #777780;
}
.module-filter button {
  padding: 0 12px;
  min-height: 36px;
  border-radius: 50px;
}
.module-filter .active {
  background: #f0edfd;
  color: #6b5dd5;
}
.coupon-sort {
  margin-left: auto;
  gap: 16px;
}
.coupon-sort .active {
  color: #6b5dd5;
}
.coupon-sort img {
  width: 13px;
  height: 13px;
  margin-left: 4px;
  vertical-align: middle;
}
.coupon-help {
  display: flex;
  gap: 5px;
  align-items: center;
}
.coupon-help img {
  width: 15px;
  height: 15px;
}
.coupon-type {
  color: #777780;
  display: flex;
  align-items: center;
  gap: 6px;
}
.coupon-type select {
  color: #777780;
  border: 1px solid #e9e9ef;
  background: transparent;
  border-radius: 6px;
  font: inherit;
  min-height: 36px;
  max-width: 100px;
}
.coupon-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.coupon-card {
  position: relative;
  padding: 24px 24px 18px;
  background: white;
  border-radius: 16px;
  min-width: 0;
  align-self: start;
}
.coupon-module {
  color: #6b5dd5;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  gap: 4px;
  min-height: 14px;
}
.coupon-benefit {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: #6b5dd5;
  min-height: 32px;
  flex-wrap: wrap;
}
.coupon-benefit strong {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #7c6ff0;
  white-space: nowrap;
}
.coupon-benefit span {
  font-size: 12px;
  line-height: 16px;
}
.coupon-divider {
  height: 1px;
  margin: 0 0 10px;
}
.coupon-divider img {
  display: block;
  width: 100%;
  height: 1px;
}
.coupon-notch {
  position: absolute;
  top: 64px;
  width: 12px;
  height: 12px;
}
.coupon-notch.left {
  left: -6px;
}
.coupon-notch.right {
  right: -6px;
}
.coupon-card h3 {
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 2px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.coupon-summary,
.coupon-expiry,
.coupon-reason {
  font-size: 12px;
  line-height: 18px;
  color: #777780;
  margin: 0;
  overflow-wrap: anywhere;
}
.coupon-reason {
  color: #8b652e;
}
.coupon-card footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
}
.coupon-card .coupon-rule-toggle {
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.coupon-rule-toggle img {
  width: 8px;
  height: 8px;
}
.coupon-use {
  background: #f0edfd;
  color: #6b5dd5;
  padding: 0 14px;
  min-height: 40px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  font-size: 13px;
  white-space: nowrap;
}
.coupon-disabled {
  font-size: 12px;
  color: #777780;
}
.unavailable .coupon-benefit strong {
  color: #777780;
}
.coupon-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  color: #777780;
  font-size: 12px;
  margin: 6px 0;
}
.coupon-pagination p {
  margin: 0;
}
.coupon-pagination > div {
  display: flex;
  align-items: center;
  gap: 8px;
}
.coupon-pagination button {
  width: 30px;
}
.coupon-pagination button:disabled {
  opacity: 0.35;
  cursor: default;
}
.coupon-pagination img {
  width: 10px;
  height: 10px;
}
.coupon-pagination button:first-child img {
  transform: rotate(90deg);
}
.coupon-pagination button:nth-child(3) img {
  transform: rotate(-90deg);
}
.current-page {
  background: #7c6ff0;
  color: white;
  border-radius: 8px;
  display: grid;
  place-items: center;
  width: 30px;
  height: 40px;
}
.coupon-hints {
  border-top: 1px solid #e9e9ef;
  padding-top: 10px;
  font-size: 12px;
  color: #777780;
  line-height: 18px;
}
.coupon-hints strong {
  font-size: 13px;
  color: #24242a;
}
.coupon-hints p {
  margin: 2px 0;
}
.coupon-state {
  background: white;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  margin-bottom: 16px;
}
.coupon-state p {
  color: #777780;
}
.coupon-state button,
.coupon-state a {
  color: #6b5dd5;
}
.coupon-state h3 {
  font-size: 18px;
}
.coupon-contact {
  color: #6b5dd5;
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: flex-end;
  min-height: 44px;
}
.coupon-contact img {
  width: 9px;
  height: 9px;
  transform: rotate(-90deg);
}
@media (max-width: 1200px) {
  .coupon-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .coupons-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .coupon-sort {
    margin-left: 0;
  }
}
@media (max-width: 950px) {
  .coupon-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .coupon-card {
    padding: 20px 16px 16px;
  }
}
@media (max-width: 700px) {
  .coupon-card.expanded {
    grid-column: 1 / -1;
  }
  .coupon-status-tabs {
    gap: 20px;
  }
  .coupon-toolbar {
    gap: 6px 12px;
  }
  .coupon-grid {
    gap: 12px;
  }
  .coupon-pagination {
    align-items: flex-start;
    flex-direction: column;
  }
  .coupons-heading h2 {
    font-size: 22px;
  }
  .coupon-card {
    padding: 20px 16px 16px;
  }
  .coupon-benefit {
    gap: 2px;
  }
  .coupon-benefit span {
    font-size: 11px;
  }
  .coupon-benefit strong {
    font-size: 24px;
  }
  .coupon-card footer {
    flex-wrap: wrap;
  }
  .coupon-card h3 {
    min-height: 40px;
  }
  .coupon-sort {
    width: 100%;
  }
  .coupon-help {
    margin-left: auto;
  }
}
@media (max-width: 370px) {
  .coupon-grid {
    grid-template-columns: 1fr;
  }
}
</style>
